import { useRovingFocus } from "./useRovingFocus";

/**
 * "view": sub-navegación con fila desplazable y pestaña activa en amarillo.
 * "cycle": segmentado (Actual · Anterior · Personalizado), activa en tinta; ancho 420px en md+.
 * "kind": selector de dos o tres opciones (Gastos / Ingresos), pestañas de 40px; activa en amarillo.
 */
type TabsVariant = "view" | "cycle" | "kind";

interface TabsItem<T extends string> {
  id: T;
  label: string;
}

export interface TabsProps<T extends string> {
  items: ReadonlyArray<TabsItem<T>>;
  /** `id` de la pestaña activa. */
  value: T;
  onChange: (id: T) => void;
  /** Nombre accesible del tablist. */
  ariaLabel: string;
  variant: TabsVariant;
  /** Prefijo de ids: cada pestaña usa `<idPrefix>-tab-<id>` y apunta a `<idPrefix>-panel-<id>`. */
  idPrefix?: string;
  /** Clases extra del tablist. */
  className?: string;
}

const TABLIST_CLASS: Record<TabsVariant, string> = {
  view: "-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
  cycle: "flex gap-2 md:w-[420px]",
  kind: "flex gap-2",
};

const BASE_TAB_CLASS =
  "inline-flex min-h-11 flex-none items-center justify-center whitespace-nowrap rounded-[var(--ds-radius-pill)] border-[1.5px] border-solid px-4 text-[15px] font-medium leading-none transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--ds-focus)]";

const KIND_TAB_CLASS =
  "app-focus-ring inline-flex h-10 flex-1 cursor-pointer items-center justify-center whitespace-nowrap rounded-full border-[1.5px] px-5 text-[15px] leading-none md:flex-none";

function tabClass(variant: TabsVariant, selected: boolean): string {
  if (variant === "kind") {
    return `${KIND_TAB_CLASS} ${
      selected
        ? "border-[var(--ds-card-sun)] bg-[var(--ds-card-sun)] font-medium text-[var(--ds-on-card)]"
        : "border-[var(--ds-ink)] bg-transparent text-[var(--ds-ink)]"
    }`;
  }
  const selectedClass =
    variant === "view"
      ? "border-transparent bg-[var(--ds-card-sun)] text-[var(--ds-on-card)]"
      : "border-[var(--ds-ink)] bg-[var(--ds-ink)] text-[var(--ds-ground)]";
  return [
    BASE_TAB_CLASS,
    variant === "cycle" ? "flex-1 md:flex-1" : "",
    selected ? selectedClass : "border-[var(--ds-line)] bg-transparent text-[var(--ds-ink)]",
  ].filter(Boolean).join(" ");
}

/** Fila de pestañas (role="tablist"/"tab") con roving tabindex, ←/→, Inicio/Fin y `aria-selected`. Objetivos de 44px salvo "kind" (40px). */
export function Tabs<T extends string>({ items, value, onChange, ariaLabel, variant, idPrefix, className }: TabsProps<T>) {
  const ids = items.map((item) => item.id);
  const { onKeyDown, setElement } = useRovingFocus(ids, onChange);

  return (
    <div role="tablist" aria-label={ariaLabel} className={className ? `${TABLIST_CLASS[variant]} ${className}` : TABLIST_CLASS[variant]}>
      {items.map((item, index) => {
        const selected = item.id === value;
        return (
          <button
            key={item.id}
            ref={setElement(index)}
            type="button"
            role="tab"
            id={idPrefix ? `${idPrefix}-tab-${item.id}` : undefined}
            aria-controls={idPrefix ? `${idPrefix}-panel-${item.id}` : undefined}
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(item.id)}
            onKeyDown={(event) => onKeyDown(event, index)}
            className={tabClass(variant, selected)}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}
